'use strict'

/**
 * Validity-window date parsing + runtime picker-state classification +
 * change-contract eligibility whitelist.
 *
 * Answers: "when is this contract usable, and can it still be changed?"
 *
 * Strict yyyy-mm-dd parsing for upstream contract validity fields. Shared
 * by draft validation (save-time) and runtime picker getters so malformed
 * dates cannot slip through as "no boundary."
 */

import {CONTRACT_PICKER_STATES} from '../constants'
import {CHANGE_CONTRACT_ALLOWED_STATUSES} from './_shared'

const CONTRACT_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/
const MS_PER_DAY = 86_400_000
const EXPIRING_SOON_DAYS = 30

/**
 * Parse a calendar date string at UTC midnight. Returns null when the value
 * is missing, not `yyyy-mm-dd`, or not a real calendar day (e.g. 2026-02-30).
 */
export const parseContractValidityDate = (value) => {
  if (!value || typeof value !== 'string') return null
  const trimmed = value.trim()
  const match = CONTRACT_DATE_RE.exec(trimmed)
  if (!match) return null
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const date = new Date(Date.UTC(year, month - 1, day))
  if (
    date.getUTCFullYear() !== year
    || date.getUTCMonth() !== month - 1
    || date.getUTCDate() !== day
  ) {
    return null
  }
  return date
}

export const isContractValidityDateString = (value) => parseContractValidityDate(value) !== null

const startOfUtcDay = (date) => {
  const d = date instanceof Date ? new Date(date.getTime()) : new Date(date)
  d.setUTCHours(0, 0, 0, 0)
  return d
}

export const isContractPreEffective = (contract, asOf = new Date()) => {
  const from = parseContractValidityDate(contract && contract.effectiveFrom)
  if (!from) return false
  return startOfUtcDay(asOf).getTime() < from.getTime()
}

export const isContractExpired = (contract, asOf = new Date()) => {
  const to = parseContractValidityDate(contract && contract.effectiveTo)
  if (!to) return false
  return startOfUtcDay(asOf).getTime() > to.getTime()
}

export const isContractEffective = (contract, asOf = new Date()) => {
  if (!contract || contract.active === false) return false
  if (!parseContractValidityDate(contract && contract.effectiveTo)) return false
  if (isContractPreEffective(contract, asOf)) return false
  if (isContractExpired(contract, asOf)) return false
  return true
}

/**
 * Contracts eligible for the master-lot contract-switch picker — wider net
 * than `isContractEffective`: active + not-yet-pre-effective, but *expired*
 * contracts pass through so an operator can still amend a settled lot's
 * contract back to its historical terms. Strictly active-only pickers
 * (create/edit forms) should use `isContractEffective` instead.
 */
export const isContractSelectableForSwitch = (contract, asOf = new Date()) => {
  if (!contract || contract.active === false) return false
  if (!parseContractValidityDate(contract && contract.effectiveTo)) return false
  if (isContractPreEffective(contract, asOf)) return false
  return true
}

/**
 * Chip-state classification for a single contract — drives the picker UI.
 * Possible states ('active' | 'expiring_soon' | 'pre_effective' | 'expired'
 * | 'invalid'). 'invalid' is returned for contracts whose effectiveTo
 * cannot be strict-parsed as yyyy-mm-dd; symmetric with isContractEffective /
 * isContractSelectableForSwitch (which both already reject malformed
 * contracts), so callers that pre-filter via those getters never see
 * 'invalid', but defensive against any future caller that runs
 * contractPickerState over the unfiltered list.
 */
export const contractPickerState = (contract, asOf = new Date()) => {
  if (!contract) return CONTRACT_PICKER_STATES.ACTIVE
  if (contract.effectiveTo && !parseContractValidityDate(contract.effectiveTo)) return CONTRACT_PICKER_STATES.INVALID
  if (isContractPreEffective(contract, asOf)) return CONTRACT_PICKER_STATES.PRE_EFFECTIVE
  if (isContractExpired(contract, asOf)) return CONTRACT_PICKER_STATES.EXPIRED
  const to = parseContractValidityDate(contract.effectiveTo)
  if (to) {
    const daysLeft = (to.getTime() - startOfUtcDay(asOf).getTime()) / MS_PER_DAY
    if (daysLeft >= 0 && daysLeft <= EXPIRING_SOON_DAYS) return CONTRACT_PICKER_STATES.EXPIRING_SOON
  }
  return CONTRACT_PICKER_STATES.ACTIVE
}

export const canChangeMasterLotContract = (status) =>
  CHANGE_CONTRACT_ALLOWED_STATUSES.has(status)
